import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Camera, X } from "lucide-react";
import { api, uploadImage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

const FUEL = ["empty", "1/4", "1/2", "3/4", "full"];
const LIGHTS = ["engine", "abs", "airbag", "oil", "battery", "coolant", "screen wash", "gearbox", "other"];

export default function CheckIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(params.get("job_id") || "");
  const [mileage, setMileage] = useState("");
  const [fuel, setFuel] = useState("1/2");
  const [lights, setLights] = useState([]);
  const [damage, setDamage] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);

  useEffect(() => { api("/jobs?status=open").then(setJobs).catch(() => {}); }, []);
  // Restore an autosaved draft for this job
  useEffect(() => {
    if (!jobId) return;
    try {
      const d = JSON.parse(localStorage.getItem(`checkin_draft_${jobId}`) || "null");
      if (d) { setMileage(d.mileage || ""); setFuel(d.fuel || "1/2"); setLights(d.lights || []); setDamage(d.damage || ""); setNotes(d.notes || ""); }
    } catch { /* ignore */ }
  }, [jobId]);
  // Autosave the draft as the user types
  useEffect(() => {
    if (!jobId) return;
    const h = setTimeout(() => {
      localStorage.setItem(`checkin_draft_${jobId}`, JSON.stringify({ mileage, fuel, lights, damage, notes }));
      setDraftSaved(true);
    }, 600);
    return () => clearTimeout(h);
  }, [jobId, mileage, fuel, lights, damage, notes]);
  const toggle = (l) => setLights((p) => p.includes(l) ? p.filter((x) => x !== l) : [...p, l]);

  const onPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const up = await uploadImage(f, { context: "checkin" });
        setPhotos((p) => [...p, { id: up.id, url: up.url }]);
      }
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!jobId) { toast.error("Select a job"); return; }
    setSaving(true);
    try {
      const res = await api("/workshop/check-ins", { method: "POST", body: JSON.stringify({ job_id: jobId, mileage: mileage ? Number(mileage) : null, fuel_level: fuel, warning_lights: lights, existing_damage: damage, notes, photos: photos.map((p) => p.id) }) });
      try { localStorage.removeItem(`checkin_draft_${jobId}`); } catch { /* ignore */ }
      if (res?.email_reply_to_warning) toast.warning(res.email_reply_to_warning);
      else if (res?.email_status === "sent") toast.success("Vehicle checked in — confirmation emailed to customer");
      else if (res?.email_status === "no_customer_email") toast.success("Vehicle checked in (no customer email on file)");
      else toast.success("Vehicle checked in");
      navigate(`/jobs/${jobId}`);
      navigate(`/jobs/${jobId}`);
    } catch (e2) { toast.error(e2.message); } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-xl">
      <Link to="/jobs" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Jobs</Link>
      <PageHeader title="Vehicle Check-In" subtitle="Record condition on arrival" />
      {draftSaved && jobId && <p data-testid="checkin-draft-indicator" className="mb-2 text-right text-[11px] font-semibold text-success">Draft saved automatically</p>}
      <form onSubmit={submit} className="space-y-4 rounded-xl border border-border bg-card p-6">
        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">JOB *</span>
          <select value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="checkin-job" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand">
            <option value="">Select a job…</option>{jobs.map((j) => <option key={j.id} value={j.id}>{j.vehicle?.vrm} — {j.title}</option>)}</select></label>
        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">MILEAGE</span>
          <input type="number" value={mileage} onChange={(e) => setMileage(e.target.value)} data-testid="checkin-mileage" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>
        <div><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">FUEL LEVEL</span>
          <div className="flex gap-2">{FUEL.map((f) => <button type="button" key={f} onClick={() => setFuel(f)} className={`flex-1 rounded-lg border py-2 text-sm font-bold ${fuel === f ? "border-brand bg-brand-tertiary text-brand-2" : "border-border text-slate-500"}`}>{f}</button>)}</div></div>
        <div><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">WARNING LIGHTS</span>
          <div className="flex flex-wrap gap-2">{LIGHTS.map((l) => <button type="button" key={l} onClick={() => toggle(l)} className={`rounded-full border px-3 py-1.5 text-xs font-bold uppercase ${lights.includes(l) ? "border-warning bg-warning-bg text-warning" : "border-border text-slate-500"}`}>{l}</button>)}</div></div>
        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">EXISTING DAMAGE</span>
          <textarea value={damage} onChange={(e) => setDamage(e.target.value)} data-testid="checkin-damage" rows={2} className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>
        <PhotoSection photos={photos} onPhoto={onPhoto} uploading={uploading} onRemove={(id) => setPhotos((p) => p.filter((x) => x.id !== id))} testid="checkin" label="ARRIVAL (BEFORE) PHOTOS" />
        <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">NOTES</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="checkin-notes" rows={2} className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>
        <button type="submit" disabled={saving} data-testid="checkin-save-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "COMPLETE CHECK-IN"}
        </button>
      </form>
    </div>
  );
}

export function PhotoSection({ photos, onPhoto, uploading, onRemove, testid, label }) {
  return (
    <div>
      <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">{label || "PHOTOS"}</span>
      <div className="flex flex-wrap gap-2">
        {photos.map((p) => (
          <div key={p.id} className="relative h-20 w-20 overflow-hidden rounded-lg border border-border" data-testid={`${testid}-photo-${p.id}`}>
            <img src={p.url} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => onRemove(p.id)} data-testid={`${testid}-photo-remove-${p.id}`} className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
          </div>
        ))}
        <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-brand bg-brand-tertiary/40 text-brand-2" data-testid={`${testid}-photo-add`}>
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Camera className="h-5 w-5" /><span className="text-[10px] font-bold">ADD</span></>}
          <input type="file" accept="image/*" capture="environment" multiple onChange={onPhoto} className="hidden" data-testid={`${testid}-photo-input`} />
        </label>
      </div>
    </div>
  );
}
